export const MARGINS = {
    top: 133,
    right: 30,
    bottom: 285,
    left: 180,
};

/**
 * Computes the width of the provided text.
 *
 * @param {String} text
 * @returns {Number} the width of the specified text in
 *  pixels as it would be displayed in 12px sans-serif.
 */
export function textWidth(text) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = "12px sans-serif";
    return Math.ceil(context.measureText(text).width);
}

/**
 * Creates <tspan> elements within the provided
 * <text> element that each contain parts of the
 * provided 'name' string. The <tspans> are created
 * to allow the name to 'word-wrap' onto new lines
 * within the <text> element
 *
 * @param {Node} textNode
 * @param {String} name
 */
export function wordWrapTextNode(textNode, name) {
    // Split out the words
    const words = name.split(/\s+/).reverse();

    // Create the first tspan
    let lineno = 0;
    let tspan = textNode
        .append("tspan")
        .attr("x", textNode.attr("x"))
        .attr("y", textNode.attr("y"))
        .attr("dy", "0em")
        .text(words.pop());

    while (words.length) {
        const word = words.pop();
        const originaltext = tspan.text();
        tspan.text(tspan.text() + " " + word);
        if (textWidth(tspan.text()) > 82) {
            tspan.text(originaltext);
            lineno += 1;
            tspan = textNode
                .append("tspan")
                .attr("x", +textNode.attr("x") + 5)
                .attr("y", +textNode.attr("y"))
                .attr("dy", `${lineno * 1.35}em`)
                .text(" " + word);
        }
    }
}

/**
 * Using the provided 'entries' and the options specified
 * in the provided 'inputs', this function computes the
 * minimum and maximum data values that will be displayed
 * on the chart. It returns those values, padded by 25%
 *
 * @param {*} entries - list of data entries (see createEntries function)
 * @param {*} inputs - list of selected options
 *
 * @returns {Object} - a simple object containing a 'min' and 'max'
 *  property.
 */
export function calculateMinMax(entries, inputs) {
    if (!entries.length) {
        return { min: 0, max: 200 };
    }

    // Compute the min/max range
    let min = 1e100;
    let max = -1e100;
    for (const entry of entries) {
        const toBeConsidered = [];

        if (inputs.showMax) {
            toBeConsidered.push(entry.maxPctOfNormal);
        }
        if (inputs.showMin) {
            toBeConsidered.push(entry.minPctOfNormal);
        }
        if (inputs.showNormal) {
            toBeConsidered.push(entry.normalPctOfNormal);
        }
        if (inputs.showObserved) {
            toBeConsidered.push(entry.obsPctOfNormal);
        }
        if (inputs.showForecast) {
            toBeConsidered.push(...entry.forecastValuesPctOfNormal);
        }
        min = Math.min(min, ...toBeConsidered.filter((v) => v !== null && isFinite(v)));
        max = Math.max(max, ...toBeConsidered.filter((v) => v !== null && isFinite(v)));
    }

    // expand min/max by a quarter of the range
    const range = max - min;
    min -= 0.125 * range;
    max += 0.125 * range;

    // Constrain to positive if needed
    if (inputs.hideNeg) {
        min = 0.0;
    }

    return { min: min, max: max };
}

/**
 * Creates a linear gradient node using stops defined by
 * the provided offsets.
 *
 * @param {String} id - node id for the linearGradient
 * @param {Array[Number]} offsets - offset percentage for the 70%, 50%, and 30% forecasts
 * @returns {linearGradientNode}
 */
export function createGradient(id, offsets, reverse) {
    // Create gradient element
    const gradient = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
    gradient.id = id;

    let colors = ["RGB(215,0,0)", "RGB(240,240,60)", "RGB(20,200,20)", "RGB(30,90,255)", "RGB(120,0,180)"];
    if (reverse) {
        colors = colors.reverse();
        offsets = offsets.reverse();
    }

    gradient.innerHTML = `
    <stop class="start" offset="0%" stop-color="${colors[0]}" stop-opacity="1"></stop>
    <stop class="start" offset="${offsets[0]}%" stop-color="${colors[1]}" stop-opacity="1"></stop>
    <stop class="start" offset="${offsets[1]}%" stop-color="${colors[2]}" stop-opacity="1"></stop>
    <stop class="start" offset="${offsets[2]}%" stop-color="${colors[3]}" stop-opacity="1"></stop>
    <stop class="stop" offset="100%" stop-color="${colors[4]}" stop-opacity="1"></stop>`;
    return gradient;
}

/**
 * Formats a value into rounded KAF
 * @param {Number} value
 * @returns {String} - formatted value;
 */
export function formatValue(value) {
    if (value === null || value === undefined) {
        return "";
    }
    if (value > 10000) {
        return Math.round(value / 1000).toString();
    }
    if (value > 1000) {
        return (Math.round(value / 100) / 10).toString();
    }
    return (Math.round(value / 10) / 100).toString();
}

/**
 * Takes a list of text nodes and adjusts the 'x' attributes
 * of the nodes to avoid overlaps between the text strings.
 * also, shifts text to avoid any label overlapping the zero-line
 * on the x-axis.
 *
 * @param {Array[Node]} nodeArray
 */
export function adjustLabels(nodeArray) {
    // Only need to re-arrange if there's more than one node
    if (nodeArray.length > 1) {
        // sort the nodes by x-position
        nodeArray = nodeArray.toSorted((a, b) => +a.getAttribute("x") - +b.getAttribute("x"));

        // get the x locations
        const xLocs = nodeArray.map((n) => +n.getAttribute("x"));

        // get labels and widths
        const labels = nodeArray.map((n) => n.innerHTML);
        const widths = labels.map(textWidth);

        // starting from the middle node, work outward and
        // adjust any nodes that overlap
        const middleIdx = Math.floor(xLocs.length / 2);
        for (let j = middleIdx; j < xLocs.length - 1; j++) {
            if (xLocs[j] + widths[j] / 2 + 6 + widths[j + 1] / 2 >= xLocs[j + 1]) {
                xLocs[j + 1] += xLocs[j] + widths[j] / 2 + 6 + widths[j + 1] / 2 - xLocs[j + 1];
            }
        }
        for (let j = middleIdx; j > 0; j--) {
            if (xLocs[j - 1] + widths[j - 1] / 2 + 6 + widths[j] / 2 >= xLocs[j]) {
                xLocs[j - 1] -= xLocs[j - 1] + widths[j - 1] / 2 + 6 + widths[j] / 2 - xLocs[j];
            }
        }

        // next, start at the left-most node and adjust all nodes to avoid
        // the first one from crossing the zero boundary
        if (xLocs[0] - 5 - widths[0] / 2 < 0) {
            xLocs[0] -= xLocs[0] - 5 - widths[0] / 2;
            for (let j = 0; j < xLocs.length - 1; j++) {
                if (xLocs[j] + widths[j] / 2 + 6 + widths[j + 1] / 2 >= xLocs[j + 1]) {
                    xLocs[j + 1] += xLocs[j] + widths[j] / 2 + 6 + widths[j + 1] / 2 - xLocs[j + 1];
                }
            }
        }

        // Finally, assign the new positions
        nodeArray.forEach((node, idx) => {
            node.setAttribute("x", xLocs[idx]);
        });
    }
}
